import Cookies from 'js-cookie';
import { ApiService } from '../api';
import { URLS, UrlUtils } from '../urls';
import { ACCESS_TOKEN_KEY } from '../../shared/constants';
import { WebUtils } from '../../utils/web_utils';
import { L10nUtils } from '../../utils/l10n_utils';
// ---------------------------------------------------------------------------
// Cross-domain OAuth contract.
//
// The provider selector and callbacks live on a single central auth domain
// (URLS.OAUTH_AUTH_ORIGIN), while the viewer runs on many client domains.
// Because the session created on the auth domain is not readable here, the
// token is handed back through a UUID-correlated postMessage relay:
//
//   1. we mint a UUID and store it in a first-party cookie on this domain,
//   2. we redirect the top-level page to `/sign_in`, passing this page as the
//      return URL and the UUID as `state`,
//   3. the auth domain runs the provider flow, mints a token keyed by `state`,
//      and redirects back to our return URL (marked with OAUTH_CALLBACK_PARAM),
//   4. on return we load the auth domain's token page in a hidden iframe with
//      the same `state`; it posts the token back to us via postMessage.
//
// The names below are the wire contract with the backend — keep them in sync.
// ---------------------------------------------------------------------------
const OAUTH_STATE_COOKIE = 'sphere_oauth_state';
const REDIRECT_URI_PARAM = 'redirect_uri';
const STATE_PARAM = 'state';
const OAUTH_CALLBACK_PARAM = 'sphere_oauth';
const OAUTH_CALLBACK_VALUE = 'callback';
const TOKEN_MESSAGE_TYPE = 'sphere-oauth-token';
const TOKEN_RETRIEVE_TIMEOUT = 15000;
/**
 * Drives the multi-domain OAuth handshake and exposes the resulting token to the
 * rest of the app through the shared `accesstoken` storage key, so an OAuth login
 * is indistinguishable from a regular email/password login downstream.
 */
export class OauthService extends ApiService {
    /**
     * Step 1 + 2: mint a correlation UUID, persist it as a first-party cookie so it
     * survives the redirect round-trip, then send the top-level page to the auth
     * selector with our return URL and the UUID as `state`.
     */
    beginSignIn() {
        const state = crypto.randomUUID();
        // Host-only cookie on purpose: it is only ever read back via JS on this exact
        // page/host (the return URL is this page's href), so it never needs to span
        // subdomains. Setting an explicit `domain` (via getCookieDomain()) is unsafe in
        // the multi-domain flow — the backend-injected cookie domain may not match the
        // client domain the viewer is running on, in which case the browser silently
        // rejects the cookie and the state is lost across the redirect round-trip.
        Cookies.set(OAUTH_STATE_COOKIE, state, {
            sameSite: "Lax" /* COOKIES_ATTRIBUTES.SAME_SITE_LAX */
        });
        const signInUrl = UrlUtils.getUrlWithQueries(URLS.OAUTH_SIGN_IN_PAGE, {
            [REDIRECT_URI_PARAM]: this.buildReturnUrl(),
            [STATE_PARAM]: state
        });
        WebUtils.redirectToSpecificUrl(signInUrl);
    }
    /** True when the current page load is the redirect back from the auth domain. */
    isReturningFromAuth() {
        return UrlUtils.getQueryValueFromUrl(OAUTH_CALLBACK_PARAM) === OAUTH_CALLBACK_VALUE;
    }
    /**
     * Step 3 + 4: exchange the stored `state` for the token minted on the auth
     * domain by opening its token page in a short-lived popup and awaiting the
     * token via postMessage. Persists the token as `accesstoken` on success.
     *
     * MUST be called synchronously from a user gesture (a click) — see
     * {@link retrieveToken} for why a popup (and therefore a gesture) is required.
     */
    completeSignIn() {
        const state = Cookies.get(OAUTH_STATE_COOKIE);
        if (!state) {
            return Promise.reject(new Error('Missing OAuth state cookie'));
        }
        return this.retrieveToken(state).then(token => {
            Cookies.remove(OAUTH_STATE_COOKIE);
            this.storeToken(token);
        });
    }
    /** Resolve the currently logged-in user from the stored token, if any. */
    getSession() {
        if (!window.localStorage.getItem(ACCESS_TOKEN_KEY)) {
            return Promise.resolve({ user: null });
        }
        const url = UrlUtils.getUrlWithQueries(URLS.USER_VERIFY_TOKEN, { lang: L10nUtils.getCurrentLanguage() });
        return this.get(url)
            .then(resp => resp.json())
            .then(data => { var _a; return ({ user: (_a = data === null || data === void 0 ? void 0 : data.user) !== null && _a !== void 0 ? _a : null }); });
    }
    /**
     * Sign the user out: revoke the session on the backend (`auth/log_out`) then
     * clear the local token so the app treats the user as signed out. The local
     * teardown runs even if the backend call fails, so a network error can't leave
     * the user stuck in a half-logged-in state. The request is fired before the
     * token is removed so it still carries the auth header.
     */
    signOut() {
        return this.get(URLS.USER_LOG_OUT)
            .then(() => undefined)
            .catch(() => undefined)
            .then(() => this.clearLocalSession());
    }
    clearLocalSession() {
        window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        Cookies.remove(OAUTH_STATE_COOKIE);
        this.notifyTokenChange(null);
    }
    /**
     * Open the auth domain's token page in a short-lived popup and resolve with the
     * token it relays back via postMessage.
     *
     * A popup (not a hidden iframe) is required: the token page reads the auth
     * domain's own session cookie to look the token up, and that cookie is only sent
     * in a first-party (top-level) context. In a cross-site iframe the browser treats
     * it as third-party and withholds it, so the relay never resolves. The cost is
     * that `window.open` only works inside a user gesture, so the whole chain that
     * reaches here must run synchronously from a click handler — no awaits before it.
     */
    retrieveToken(state) {
        return new Promise((resolve, reject) => {
            const popup = window.open(UrlUtils.getUrlWithQueries(URLS.OAUTH_TOKEN_PAGE, { [STATE_PARAM]: state }), 'sphere-oauth', 'width=100,height=100');
            if (!popup) {
                reject(new Error('OAuth token popup was blocked'));
                return;
            }
            let settled = false;
            let timer = null;
            let closeWatcher = null;
            const cleanup = () => {
                if (timer !== null) {
                    window.clearTimeout(timer);
                }
                if (closeWatcher !== null) {
                    window.clearInterval(closeWatcher);
                }
                window.removeEventListener('message', onMessage);
                if (!popup.closed) {
                    popup.close();
                }
            };
            const finish = (settle) => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                settle();
            };
            const onMessage = (event) => {
                if (event.origin !== URLS.OAUTH_AUTH_ORIGIN) {
                    return;
                }
                const data = event.data;
                if (!data || data.type !== TOKEN_MESSAGE_TYPE) {
                    return;
                }
                finish(() => data.token ? resolve(data.token) : reject(new Error(data.error || 'OAuth token relay failed')));
            };
            timer = window.setTimeout(() => finish(() => reject(new Error('OAuth token relay timed out'))), TOKEN_RETRIEVE_TIMEOUT);
            // The bridge closes itself right after posting the token, so a "closed"
            // popup usually means success is already in flight — give any trailing
            // message a beat to arrive before treating a manual close as a cancel.
            closeWatcher = window.setInterval(() => {
                if (!popup.closed || settled) {
                    return;
                }
                window.clearInterval(closeWatcher);
                closeWatcher = null;
                window.setTimeout(() => finish(() => reject(new Error('OAuth sign-in was cancelled'))), 400);
            }, 500);
            window.addEventListener('message', onMessage);
        });
    }
    storeToken(token) {
        window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
        this.notifyTokenChange(token);
    }
    /**
     * Same-tab `localStorage` writes don't fire the native `storage` event, so
     * replay one manually to let the main Account overlay (which already listens
     * for it) verify the token and log the whole app in.
     */
    notifyTokenChange(token) {
        window.dispatchEvent(new StorageEvent('storage', {
            key: ACCESS_TOKEN_KEY,
            newValue: token,
            storageArea: window.localStorage
        }));
    }
    buildReturnUrl() {
        const url = new URL(window.location.href);
        url.searchParams.set(OAUTH_CALLBACK_PARAM, OAUTH_CALLBACK_VALUE);
        return url.toString();
    }
    /** Strip the `?sphere_oauth=callback` marker so a refresh doesn't re-trigger the return flow. */
    consumeReturnMarker() {
        const url = new URL(window.location.href);
        url.searchParams.delete(OAUTH_CALLBACK_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
    }
}
